import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  MapPin, Trash2, CheckCircle, Filter, Plus, ChevronDown, ChevronUp,
  Search, Building, User, Phone, Mail, Globe, Wifi, Zap, Cpu,
  Eye, X, Pin, FileText, Check, Award
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CampusDetailDrawer = ({ campus, onClose, onUpdateStatus }) => {
  if (!campus) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 07 &bull; Physical Hub
              </span>
              <span className="text-xs font-mono font-bold text-amber-400">
                {campus.campusType || 'Primary Campus'}
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{campus.name}</h2>
            <p className="text-xs text-zinc-400 font-bold mt-1 flex items-center gap-2">
              <MapPin size={14} className="text-orange-400" /> {campus.city || campus.location || 'Nairobi'}, {campus.county || 'Nairobi'}, {campus.country || 'Kenya'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {campus.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Workstations</span>
              <span className="text-xs font-bold text-white font-mono">
                {campus.hardware?.workstations || campus.workstations || 0} Units
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Bandwidth</span>
              <span className="text-xs font-bold text-orange-400 font-mono">
                {campus.infrastructure?.bandwidthMbps || campus.bandwidthMbps || 100} Mbps
              </span>
            </div>
          </div>

          {/* Section 1: Campus Identity */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Physical Campus Profile
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Campus Name:</span>
                <span className="text-zinc-200 font-bold">{campus.name}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Campus Classification:</span>
                <span className="text-zinc-200">{campus.campusType || 'Regional Hub'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Physical Address:</span>
                <span className="text-zinc-200">{campus.address || campus.location || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">City &amp; County:</span>
                <span className="text-zinc-200">{campus.city || '-'}, {campus.county || 'Nairobi'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">GPS Coordinates:</span>
                <span className="text-zinc-200 font-mono">{campus.gpsCoordinates || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Accreditation Charter:</span>
                <span className="text-zinc-200 font-mono">{campus.accreditationCharter || 'TVETA Accredited'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Leadership & Direct Contacts */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Campus Leadership &amp; Tech Staff
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Campus Director / Principal:</span>
                <span className="text-zinc-200 font-bold">{campus.directorName || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Director Email:</span>
                <span className="text-zinc-200 font-mono">{campus.directorEmail || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Emergency Phone:</span>
                <span className="text-zinc-200 font-mono">{campus.directorPhone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Lead Lab Technician:</span>
                <span className="text-zinc-200">{campus.leadTechnicianName || '-'} ({campus.leadTechnicianPhone || '-'})</span>
              </div>
            </div>
          </div>

          {/* Section 3: Facilities & Power */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Zap size={14} /> Facilities, Capacity &amp; Power Resiliency
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Lecture Rooms / Classrooms:</span>
                <span className="text-zinc-200 font-mono font-bold">{campus.facilities?.classrooms || campus.classrooms || 0} Rooms</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Auditorium Seating:</span>
                <span className="text-zinc-200 font-mono font-bold">{campus.facilities?.auditoriumCapacity || campus.auditoriumCapacity || 0} Seats</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Total Student Capacity:</span>
                <span className="text-zinc-200 font-mono font-bold">{campus.studentCapacity || 500} Students</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Dormitory Beds:</span>
                <span className="text-zinc-200 font-mono">{campus.dormitoryCapacity || 0} Beds</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Power Backup:</span>
                <span className="text-zinc-200">{campus.facilities?.powerBackup || campus.powerBackup || 'Generator'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Generator Capacity:</span>
                <span className="text-zinc-200 font-mono font-bold">{campus.generatorKva || '50 KVA'}</span>
              </div>
            </div>
          </div>

          {/* Section 4: Hardware & Network */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Cpu size={14} /> Computing Hardware &amp; Connectivity
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Primary ISP:</span>
                <span className="text-zinc-200 font-bold">{campus.infrastructure?.primaryISP || campus.primaryISP || 'Safaricom Dedicated'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Backup ISP:</span>
                <span className="text-zinc-200">{campus.infrastructure?.backupISP || campus.backupISP || 'Liquid Telecom'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Workstation RAM Baseline:</span>
                <span className="text-zinc-200 font-mono">{campus.hardware?.baselineRAM || campus.baselineRAM || '16GB'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Dedicated GPU / VRAM:</span>
                <span className="text-zinc-200 font-mono">{campus.hardware?.vramSpecs || campus.vramSpecs || 'RTX 4070 12GB'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-zinc-500 font-bold block">Standard Operating System:</span>
                <span className="text-zinc-200">{campus.hardware?.operatingSystem || campus.operatingSystem || 'Ubuntu Linux / Windows Dual Boot'}</span>
              </div>
            </div>
          </div>

          {/* Admin Status Switcher */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Campus Status</span>
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus(campus.id, 'Active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${campus.status === 'Active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Active
              </button>
              <button 
                onClick={() => onUpdateStatus(campus.id, 'Maintenance')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${campus.status === 'Maintenance' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Maintenance
              </button>
              <button 
                onClick={() => onUpdateStatus(campus.id, 'Inactive')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${campus.status === 'Inactive' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CampusManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [campuses, setCampuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedCampus, setSelectedCampus] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('name');

    // Comprehensive Registration Form State (Matching 100% of campus & hub onboarding)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [campusType, setCampusType] = useState('Primary Campus');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('Nairobi');
    const [country, setCountry] = useState('Kenya');
    const [gpsCoordinates, setGpsCoordinates] = useState('');

    const [directorName, setDirectorName] = useState('');
    const [directorEmail, setDirectorEmail] = useState('');
    const [directorPhone, setDirectorPhone] = useState('');
    const [leadTechnicianName, setLeadTechnicianName] = useState('');
    const [leadTechnicianPhone, setLeadTechnicianPhone] = useState('');

    const [classrooms, setClassrooms] = useState(12);
    const [auditoriumCapacity, setAuditoriumCapacity] = useState(300);
    const [studentCapacity, setStudentCapacity] = useState(600);
    const [dormitoryCapacity, setDormitoryCapacity] = useState(150);
    const [wheelchairAccess, setWheelchairAccess] = useState('Yes');

    const [powerBackup, setPowerBackup] = useState('Dual Grid + Generator');
    const [generatorKva, setGeneratorKva] = useState('100 KVA');
    const [primaryISP, setPrimaryISP] = useState('Safaricom Dedicated Fiber');
    const [backupISP, setBackupISP] = useState('Liquid Intelligent Technologies');
    const [bandwidthMbps, setBandwidthMbps] = useState(250);

    const [workstations, setWorkstations] = useState(120);
    const [baselineRAM, setBaselineRAM] = useState('32GB DDR5');
    const [vramSpecs, setVramSpecs] = useState('NVIDIA RTX 4080 16GB');
    const [operatingSystem, setOperatingSystem] = useState('Ubuntu Linux / Windows Dual Boot');

    const [accreditationCharter, setAccreditationCharter] = useState('TVETA/PUB/2023/102');
    const [campusPhotoUrl, setCampusPhotoUrl] = useState('');
    const [labPhotoUrl, setLabPhotoUrl] = useState('');

    const fetchCampuses = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'campuses'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'teau-main-campus',
                    name: 'The East African University Campus',
                    campusType: 'Primary Campus',
                    address: 'Kajiado / Kitengela Road, Main Complex',
                    city: 'Kitengela',
                    county: 'Kajiado',
                    country: 'Kenya',
                    gpsCoordinates: '-1.4883, 36.9583',
                    directorName: 'Prof. Christopher Kimani',
                    directorEmail: 'vc@teau.ac.ke',
                    directorPhone: '+254 722 000 333',
                    leadTechnicianName: 'Eng. John Mwangi',
                    leadTechnicianPhone: '+254 711 999 888',
                    studentCapacity: 1200,
                    dormitoryCapacity: 450,
                    facilities: { classrooms: 24, auditoriumCapacity: 600, powerBackup: 'Dual Grid + Generator', wheelchairAccess: true },
                    infrastructure: { primaryISP: 'Safaricom Dedicated 500Mbps', backupISP: 'Liquid Intelligent', bandwidthMbps: 500 },
                    hardware: { workstations: 200, baselineRAM: '32GB DDR5', vramSpecs: 'NVIDIA RTX 4080', operatingSystem: 'Dual Boot Windows/Linux' },
                    generatorKva: '250 KVA',
                    accreditationCharter: 'CUE/UNIV/2012/08',
                    status: 'Active',
                    addedAt: '2026-06-01T08:00:00Z'
                  },
                  {
                    id: 'baringo-digital-hub',
                    name: 'Baringo County Central Innovation Hub',
                    campusType: 'Regional Hub',
                    address: 'Governor Plaza, 3rd Floor, Kabarnet',
                    city: 'Kabarnet',
                    county: 'Baringo',
                    country: 'Kenya',
                    gpsCoordinates: '0.4919, 35.7431',
                    directorName: 'Eng. Benjamin Cheboi Office',
                    directorEmail: 'ict@baringo.go.ke',
                    directorPhone: '+254 720 000 001',
                    leadTechnicianName: 'Charles Kaloki',
                    leadTechnicianPhone: '+254 712 345 678',
                    studentCapacity: 350,
                    dormitoryCapacity: 0,
                    facilities: { classrooms: 8, auditoriumCapacity: 200, powerBackup: 'Solar + Generator Backup', wheelchairAccess: true },
                    infrastructure: { primaryISP: 'NOFBI National Fiber', backupISP: 'Safaricom 5G Fixed', bandwidthMbps: 150 },
                    hardware: { workstations: 80, baselineRAM: '16GB', vramSpecs: 'NVIDIA RTX 3060', operatingSystem: 'Ubuntu Linux' },
                    generatorKva: '80 KVA',
                    accreditationCharter: 'TVETA/CG-BAR/2024',
                    status: 'Active',
                    addedAt: '2026-07-15T10:00:00Z'
                  }
                );
            }
            setCampuses(data);
        } catch (e) {
            console.error('Error fetching campuses:', e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchCampuses();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setStatusMessage('saving');

        const campusId = name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

        try {
            await setDoc(doc(db, 'campuses', campusId), {
                name: name.trim(),
                campusType,
                address: address.trim(),
                location: address.trim(),
                city: city.trim(),
                county: county.trim(),
                country: country.trim(),
                gpsCoordinates: gpsCoordinates.trim(),
                directorName: directorName.trim(),
                directorEmail: directorEmail.trim().toLowerCase(),
                directorPhone: directorPhone.trim(),
                leadTechnicianName: leadTechnicianName.trim(),
                leadTechnicianPhone: leadTechnicianPhone.trim(),
                studentCapacity: parseInt(studentCapacity) || 0,
                dormitoryCapacity: parseInt(dormitoryCapacity) || 0,
                facilities: {
                    classrooms: parseInt(classrooms) || 0,
                    auditoriumCapacity: parseInt(auditoriumCapacity) || 0,
                    powerBackup,
                    wheelchairAccess: wheelchairAccess === 'Yes'
                },
                infrastructure: {
                    primaryISP: primaryISP.trim(),
                    backupISP: backupISP.trim(),
                    bandwidthMbps: parseInt(bandwidthMbps) || 0
                },
                hardware: {
                    workstations: parseInt(workstations) || 0,
                    baselineRAM: baselineRAM.trim(),
                    vramSpecs: vramSpecs.trim(),
                    operatingSystem
                },
                generatorKva: generatorKva.trim(),
                accreditationCharter: accreditationCharter.trim(),
                media: {
                    campusPhotoUrl: campusPhotoUrl.trim(),
                    labPhotoUrl: labPhotoUrl.trim()
                },
                status: 'Active',
                updatedAt: new Date().toISOString(),
                addedAt: new Date().toISOString()
            }, { merge: true });

            setName('');
            setAddress('');
            setCity('');
            setDirectorName('');
            setDirectorEmail('');
            setDirectorPhone('');
            setIsFormOpen(false);

            setStatusMessage('success');
            fetchCampuses();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error('Error adding campus:', e);
            setStatusMessage('error');
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
      try {
        await setDoc(doc(db, 'campuses', id), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setCampuses(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
        if (selectedCampus && selectedCampus.id === id) {
          setSelectedCampus(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this campus?')) return;
        try {
            await deleteDoc(doc(db, 'campuses', id));
            if (selectedCampus?.id === id) setSelectedCampus(null);
            fetchCampuses();
        } catch (e) {
            console.error(e);
        }
    };

    const filteredCampuses = campuses.filter(c => {
      const matchesType = filterType === 'all' || (c.campusType || '').toLowerCase() === filterType.toLowerCase();
      const query = searchTerm.toLowerCase();
      const cName = (c.name || '').toLowerCase();
      const cLoc = (c.city || c.location || c.county || '').toLowerCase();
      const cDirector = (c.directorName || '').toLowerCase();
      const matchesSearch = !searchTerm || cName.includes(query) || cLoc.includes(query) || cDirector.includes(query);
      return matchesType && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedCampuses = [...filteredCampuses].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-xl">
                                <MapPin size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 07 &bull; Physical Infrastructure
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Campus &amp; Hardware Hubs</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            University workshops, regional computing laboratories, high-speed fiber infrastructure, and physical student venues.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-orange-600 hover:bg-orange-700 text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Building size={16} /> {isFormOpen ? 'Close Form' : 'Register Physical Campus'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Registration Form */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <Building size={18} /> Register Campus Infrastructure &bull; Complete Specifications
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record classrooms, auditorium, fiber SLAs, generator power, workstation GPU specs, and leadership.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleAdd} className="space-y-6">
                      {/* Section 1: Campus Identity */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <Building size={14} /> 1. Campus Identity &amp; Physical Location
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Campus Name *</label>
                                  <input 
                                      type="text" 
                                      value={name} 
                                      onChange={e => setName(e.target.value)} 
                                      placeholder="e.g. The East African University Main Campus" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Campus Type</label>
                                  <select 
                                      value={campusType} 
                                      onChange={e => setCampusType(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500 font-bold"
                                  >
                                      <option>Primary Campus</option>
                                      <option>Regional Hub</option>
                                      <option>Satellite Campus</option>
                                      <option>Innovation Center</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">City / Town *</label>
                                  <input 
                                      type="text" 
                                      value={city} 
                                      onChange={e => setCity(e.target.value)} 
                                      placeholder="e.g. Kitengela / Kabarnet" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Physical Street Address *</label>
                                  <input 
                                      type="text" 
                                      value={address} 
                                      onChange={e => setAddress(e.target.value)} 
                                      placeholder="e.g. Namanga Highway, Block A" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">County *</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">GPS Coordinates</label>
                                  <input 
                                      type="text" 
                                      value={gpsCoordinates} 
                                      onChange={e => setGpsCoordinates(e.target.value)} 
                                      placeholder="-1.4883, 36.9583" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Leadership & Contacts */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <User size={14} /> 2. Campus Leadership &amp; Direct Support Staff
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Campus Director / Principal Name *</label>
                                  <input 
                                      type="text" 
                                      value={directorName} 
                                      onChange={e => setDirectorName(e.target.value)} 
                                      placeholder="e.g. Prof. Christopher Kimani" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Director Email *</label>
                                  <input 
                                      type="email" 
                                      value={directorEmail} 
                                      onChange={e => setDirectorEmail(e.target.value)} 
                                      placeholder="director@campus.ac.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Direct Campus Phone *</label>
                                  <input 
                                      type="tel" 
                                      value={directorPhone} 
                                      onChange={e => setDirectorPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Lead Lab Technician Name</label>
                                  <input 
                                      type="text" 
                                      value={leadTechnicianName} 
                                      onChange={e => setLeadTechnicianName(e.target.value)} 
                                      placeholder="e.g. Eng. John Mwangi" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Technician Phone</label>
                                  <input 
                                      type="tel" 
                                      value={leadTechnicianPhone} 
                                      onChange={e => setLeadTechnicianPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Accreditation Charter No</label>
                                  <input 
                                      type="text" 
                                      value={accreditationCharter} 
                                      onChange={e => setAccreditationCharter(e.target.value)} 
                                      placeholder="e.g. CUE/UNIV/2012/08" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Academic Capacity & Facilities */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <Building size={14} /> 3. Lecture Rooms, Auditorium &amp; Housing Capacity
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Lecture Classrooms</label>
                                  <input 
                                      type="number" 
                                      value={classrooms} 
                                      onChange={e => setClassrooms(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Auditorium Seats</label>
                                  <input 
                                      type="number" 
                                      value={auditoriumCapacity} 
                                      onChange={e => setAuditoriumCapacity(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Total Student Capacity</label>
                                  <input 
                                      type="number" 
                                      value={studentCapacity} 
                                      onChange={e => setStudentCapacity(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Dormitory Beds</label>
                                  <input 
                                      type="number" 
                                      value={dormitoryCapacity} 
                                      onChange={e => setDormitoryCapacity(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Wheelchair Accessible</label>
                                  <select 
                                      value={wheelchairAccess} 
                                      onChange={e => setWheelchairAccess(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500 font-bold"
                                  >
                                      <option>Yes</option>
                                      <option>No</option>
                                  </select>
                              </div>
                          </div>
                      </div>

                      {/* Section 4: Power & Network */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <Zap size={14} /> 4. Power Resiliency &amp; Dedicated Fiber Connectivity
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Power Backup Architecture</label>
                                  <select 
                                      value={powerBackup} 
                                      onChange={e => setPowerBackup(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500 font-bold"
                                  >
                                      <option>Dual Grid + Generator</option>
                                      <option>Generator Only</option>
                                      <option>Solar + Inverter Backup</option>
                                      <option>None (Grid Only)</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Generator KVA Rating</label>
                                  <input 
                                      type="text" 
                                      value={generatorKva} 
                                      onChange={e => setGeneratorKva(e.target.value)} 
                                      placeholder="e.g. 150 KVA" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Primary Fiber ISP *</label>
                                  <input 
                                      type="text" 
                                      value={primaryISP} 
                                      onChange={e => setPrimaryISP(e.target.value)} 
                                      placeholder="e.g. Safaricom Business" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Bandwidth (Mbps) *</label>
                                  <input 
                                      type="number" 
                                      value={bandwidthMbps} 
                                      onChange={e => setBandwidthMbps(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-xs focus:outline-none focus:border-orange-500"
                                      required
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 5: Computing Hardware */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-orange-400 flex items-center gap-2">
                              <Cpu size={14} /> 5. Laboratory Workstations &amp; GPU Specs
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Total Lab Workstations</label>
                                  <input 
                                      type="number" 
                                      value={workstations} 
                                      onChange={e => setWorkstations(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">RAM Baseline</label>
                                  <input 
                                      type="text" 
                                      value={baselineRAM} 
                                      onChange={e => setBaselineRAM(e.target.value)} 
                                      placeholder="e.g. 32GB DDR5" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Dedicated GPU / VRAM</label>
                                  <input 
                                      type="text" 
                                      value={vramSpecs} 
                                      onChange={e => setVramSpecs(e.target.value)} 
                                      placeholder="e.g. NVIDIA RTX 4080 16GB" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Operating System</label>
                                  <select 
                                      value={operatingSystem} 
                                      onChange={e => setOperatingSystem(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-orange-500 font-bold"
                                  >
                                      <option>Ubuntu Linux / Windows Dual Boot</option>
                                      <option>Ubuntu Linux Only</option>
                                      <option>Windows 11 Pro</option>
                                  </select>
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-orange-600 hover:bg-orange-700 text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Registering Hub...' : 'Register Physical Campus Hub'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Campus facility registered and synchronized with Firestore.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by campus name, county, city, or director..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Types</option>
                            <option value="primary campus">Primary Campus</option>
                            <option value="regional hub">Regional Hub</option>
                            <option value="satellite campus">Satellite Campus</option>
                            <option value="innovation center">Innovation Center</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchCampuses}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Campus Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'name' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-orange-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Campus Name</span> {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'name' ? 'none' : 'name')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'name' ? 'text-orange-400' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'name' ? 'Unpin column' : 'Pin Campus Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'name' ? 'fill-orange-400' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Classification */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Campus Type
                              </th>

                              {/* Location */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Location (County / City)
                              </th>

                              {/* Physical Address */}
                              <th className="min-w-[200px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Physical Address
                              </th>

                              {/* Director */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Campus Director
                              </th>

                              {/* Director Contact */}
                              <th className="min-w-[170px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Contact &amp; Email
                              </th>

                              {/* Classrooms & Auditorium */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Classrooms &amp; Hall
                              </th>

                              {/* Student Capacity */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Student Seats
                              </th>

                              {/* Workstations */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Lab Units
                              </th>

                              {/* Hardware Specs */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Hardware Specs
                              </th>

                              {/* Bandwidth & ISP */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Dedicated ISP &amp; Mbps
                              </th>

                              {/* Power Backup */}
                              <th className="min-w-[150px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Power Resiliency
                              </th>

                              {/* Accreditation */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Accreditation
                              </th>

                              {/* Status */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">Loading Campus Infrastructure...</td></tr>
                          ) : sortedCampuses.length === 0 ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">No campuses found matching criteria.</td></tr>
                          ) : sortedCampuses.map((campus, idx) => {
                              return (
                                <tr 
                                    key={campus.id} 
                                    onClick={() => setSelectedCampus(campus)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'name' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-orange-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-orange-400 transition-colors">{campus.name}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{campus.campusType || 'Primary'}</p>
                                    </td>

                                    {/* Classification */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {campus.campusType || 'Regional Hub'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 font-semibold">
                                        {campus.city || campus.location || 'Nairobi'}, {campus.county || 'Nairobi'}
                                    </td>

                                    {/* Address */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400 max-w-[200px] truncate" title={campus.address || campus.location}>
                                        {campus.address || campus.location || '-'}
                                    </td>

                                    {/* Director */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{campus.directorName || '-'}</p>
                                        <p className="text-[10px] text-zinc-500">{campus.directorPhone || '-'}</p>
                                    </td>

                                    {/* Email */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        {campus.directorEmail || '-'}
                                    </td>

                                    {/* Classrooms */}
                                    <td className="p-3 border-r border-zinc-800 font-mono">
                                        <span className="text-white font-bold">{campus.facilities?.classrooms || campus.classrooms || 0} Classrooms</span>
                                        <span className="block text-[10px] text-zinc-500">{campus.facilities?.auditoriumCapacity || campus.auditoriumCapacity || 0} Hall</span>
                                    </td>

                                    {/* Student Capacity */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-orange-400 font-bold">
                                        {campus.studentCapacity || 500} Seats
                                    </td>

                                    {/* Workstations */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-teal-300 font-bold">
                                        {campus.hardware?.workstations || campus.workstations || 0} Units
                                    </td>

                                    {/* Hardware Specs */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px]">
                                        <span className="text-white">{campus.hardware?.baselineRAM || campus.baselineRAM || '16GB'}</span>
                                        <span className="block text-[10px] text-zinc-400">{campus.hardware?.vramSpecs || campus.vramSpecs || 'RTX 3060'}</span>
                                    </td>

                                    {/* Bandwidth */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px]">
                                        <span className="text-orange-400 font-bold">{campus.infrastructure?.bandwidthMbps || campus.bandwidthMbps || 100} Mbps</span>
                                        <span className="block text-[10px] text-zinc-500 truncate max-w-[150px]">{campus.infrastructure?.primaryISP || campus.primaryISP || '-'}</span>
                                    </td>

                                    {/* Power */}
                                    <td className="p-3 border-r border-zinc-800 text-[11px]">
                                        <span className="text-zinc-200">{campus.facilities?.powerBackup || campus.powerBackup || 'Generator'}</span>
                                        <span className="block text-[10px] text-zinc-500 font-mono">{campus.generatorKva || '50 KVA'}</span>
                                    </td>

                                    {/* Accreditation */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        {campus.accreditationCharter || 'Accredited'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          campus.status === 'Active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {campus.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedCampus(campus)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Campus Dossier"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(campus.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Campus"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <CampusDetailDrawer 
              campus={selectedCampus} 
              onClose={() => setSelectedCampus(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default CampusManager;
